import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminInvoices,
  setInvoiceStatus,
  createAdminInvoice,
  editAdminInvoice,
  deleteAdminInvoice,
  type CreateInvoiceInput,
} from "@/services/adminService";
import { sendErpEmail } from "@/services/erpEmailService";
import type { ErpInvoice, ErpInvoiceStatus } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import { useAdminCounters } from "@/pages/portal/admin/context/AdminCountersContext";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminStatCard from "@/pages/portal/admin/components/AdminStatCard";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import InvoicePrintModal from "@/pages/portal/admin/components/InvoicePrintModal";
import {
  adminCard, adminTh, adminTd, adminIconBtn, adminBtnGhost, adminBtnPrimary, adminBtnDanger, adminInput, adminLabel,
} from "@/pages/portal/admin/config/ui";
import { downloadCsv } from "@/utils/csv";

const STATUSES: ErpInvoiceStatus[] = ["draft", "sent", "paid", "overdue", "void"];
const CREATE_STATUSES: ErpInvoiceStatus[] = ["draft", "sent", "paid"];

const STATUS_BADGE: Record<ErpInvoiceStatus, string> = {
  draft: "bg-background-200 text-foreground-600",
  sent: "bg-secondary-100 text-secondary-800",
  paid: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-700",
  void: "bg-background-200 text-foreground-500",
};

const fmt = (n: number) => n.toLocaleString("en-IN");
const fmtDate = (d: string) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const TAX_RATE = 0.18;

/** yyyy-mm-dd for the native date input, defaulting 30 days out. */
const defaultDueDate = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
const toDateInput = (iso: string) => new Date(iso).toISOString().slice(0, 10);

interface InvoiceForm {
  customer: string;
  customerEmail: string;
  business: string;
  plan: string;
  amount: number;
  status: ErpInvoiceStatus;
  dueDate: string;
}

type Notice = { kind: "success" | "error"; text: string } | null;

export default function InvoicesSection() {
  const { accessToken } = useAuth();
  const { refresh: refreshCounters } = useAdminCounters();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminInvoices(accessToken),
    [] as ErpInvoice[],
    [accessToken],
  );
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | ErpInvoiceStatus>("all");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const [form, setForm] = useState<InvoiceForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<ErpInvoice | null>(null);
  const [printTarget, setPrintTarget] = useState<ErpInvoice | null>(null);

  const stats = useMemo(() => {
    const paid = data.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);
    const outstanding = data.filter((i) => i.status === "sent" || i.status === "overdue").reduce((s, i) => s + i.total, 0);
    const overdue = data.filter((i) => i.status === "overdue").reduce((s, i) => s + i.total, 0);
    return { paid, outstanding, overdue, count: data.length };
  }, [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((i) => {
      const matchStatus = status === "all" || i.status === status;
      const matchQ = !q || [i.invoiceNo, i.customer, i.business, i.plan].some((v) => v.toLowerCase().includes(q));
      return matchStatus && matchQ;
    });
  }, [data, search, status]);

  const previewTax = Math.round((form?.amount ?? 0) * TAX_RATE);
  const previewTotal = (form?.amount ?? 0) + previewTax;

  async function apply(inv: ErpInvoice, next: ErpInvoiceStatus) {
    setBusyId(inv.id);
    setData((prev) => prev.map((i) => (i.id === inv.id ? { ...i, status: next } : i)));
    try {
      await setInvoiceStatus(accessToken, inv.id, next);
      refreshCounters();
    } catch {
      setData((prev) => prev.map((i) => (i.id === inv.id ? { ...i, status: inv.status } : i)));
    } finally {
      setBusyId(null);
    }
  }

  function openCreate() {
    setFormError("");
    setEditId(null);
    setForm({ customer: "", customerEmail: "", business: "", plan: "", amount: 0, status: "draft", dueDate: defaultDueDate() });
  }

  function openEdit(inv: ErpInvoice) {
    setFormError("");
    setEditId(inv.id);
    setForm({
      customer: inv.customer,
      customerEmail: inv.customerEmail ?? "",
      business: inv.business,
      plan: inv.plan,
      amount: inv.amount,
      status: inv.status,
      dueDate: toDateInput(inv.dueAt),
    });
  }

  function closeForm() {
    setForm(null);
    setEditId(null);
    setFormError("");
  }

  const set = <K extends keyof InvoiceForm>(key: K, value: InvoiceForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  function buildPayload(f: InvoiceForm): CreateInvoiceInput {
    return {
      customer: f.customer,
      customerEmail: f.customerEmail,
      business: f.business,
      plan: f.plan,
      amount: f.amount,
      status: f.status,
      dueAt: f.dueDate ? new Date(`${f.dueDate}T00:00:00`).toISOString() : undefined,
    };
  }

  async function submit() {
    if (!form?.customer?.trim()) return;
    setSaving(true);
    setFormError("");
    try {
      if (editId) {
        const updated = await editAdminInvoice(accessToken, editId, buildPayload(form));
        setData((prev) => prev.map((i) => (i.id === editId ? updated : i)));
        setNotice({ kind: "success", text: `Invoice ${updated.invoiceNo} updated.` });
      } else {
        const created = await createAdminInvoice(accessToken, buildPayload(form));
        setData((prev) => [created, ...prev]);
        setNotice({ kind: "success", text: `Invoice ${created.invoiceNo} created.` });
      }
      closeForm();
      refreshCounters();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Could not save the invoice.");
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteAdminInvoice(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setNotice({ kind: "success", text: `Invoice ${deleteTarget.invoiceNo} deleted.` });
      setDeleteTarget(null);
      refreshCounters();
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof Error ? err.message : "Could not delete the invoice." });
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  async function sendEmail(inv: ErpInvoice) {
    setBusyId(inv.id);
    setNotice(null);
    try {
      const msg = await sendErpEmail(accessToken, {
        type: "invoice",
        to: inv.customerEmail ?? "",
        reference: inv.invoiceNo,
        customer: inv.customer,
        business: inv.business,
        plan: inv.plan,
        amount: inv.amount,
        tax: inv.tax,
        total: inv.total,
        currency: inv.currency,
        status: inv.status,
        issuedAt: inv.issuedAt,
        dueAt: inv.dueAt,
      });
      setNotice({ kind: "success", text: msg });
      if (inv.status === "draft") {
        const updated = await editAdminInvoice(accessToken, inv.id, { status: "sent" });
        setData((prev) => prev.map((i) => (i.id === inv.id ? updated : i)));
        refreshCounters();
      }
    } catch (err) {
      setNotice({ kind: "error", text: err instanceof Error ? err.message : "Could not send the email." });
    } finally {
      setBusyId(null);
    }
  }

  function exportCsv() {
    downloadCsv(
      `erp-invoices-${new Date().toISOString().slice(0, 10)}`,
      ["Invoice No", "Customer", "Email", "Business", "Plan", "Subtotal", "Tax", "Total", "Currency", "Status", "Order", "Issued", "Due"],
      filtered.map((i) => [i.invoiceNo, i.customer, i.customerEmail ?? "", i.business, i.plan, i.amount, i.tax, i.total, i.currency, i.status, i.orderNo ?? "", fmtDate(i.issuedAt), fmtDate(i.dueAt)]),
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard label="Total Invoices" value={loading ? "—" : fmt(stats.count)} icon="ri-file-list-3-line" accent="primary" />
        <AdminStatCard label="Paid" value={loading ? "—" : `₹${fmt(stats.paid)}`} icon="ri-checkbox-circle-line" accent="emerald" />
        <AdminStatCard label="Outstanding" value={loading ? "—" : `₹${fmt(stats.outstanding)}`} icon="ri-hourglass-line" accent="amber" sub="sent or overdue" />
        <AdminStatCard label="Overdue" value={loading ? "—" : `₹${fmt(stats.overdue)}`} icon="ri-alarm-warning-line" accent="rose" />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search invoice no, customer…" />
        <div className="flex items-center gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as "all" | ErpInvoiceStatus)}
            className="h-10 w-full cursor-pointer rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-700 outline-none focus:border-primary-400 sm:w-48"
            aria-label="Filter by invoice status"
          >
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || filtered.length === 0}
            className={`${adminBtnGhost} whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <i className="ri-download-2-line text-base" /> Export CSV
          </button>
          <button type="button" onClick={openCreate} className={`${adminBtnPrimary} whitespace-nowrap`}>
            <i className="ri-add-line text-base" /> Add invoice
          </button>
        </div>
      </div>

      {notice && (
        <div
          className={`flex items-start justify-between gap-3 rounded-lg px-4 py-3 text-sm ${
            notice.kind === "success" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
          }`}
        >
          <span className="flex items-start gap-2.5">
            <i className={`${notice.kind === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} mt-0.5 text-base`} />
            {notice.text}
          </span>
          <button type="button" onClick={() => setNotice(null)} className="cursor-pointer text-current opacity-70 hover:opacity-100">
            <i className="ri-close-line text-base" />
          </button>
        </div>
      )}

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] border-collapse">
            <thead className="border-b border-background-200 bg-background-100/60">
              <tr>
                <th className={adminTh}>Invoice</th>
                <th className={adminTh}>Billed to</th>
                <th className={adminTh}>Plan</th>
                <th className={adminTh}>Total</th>
                <th className={adminTh}>Status</th>
                <th className={adminTh}>Order</th>
                <th className={adminTh}>Due</th>
                <th className={`${adminTh} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading ? (
                <tr><td colSpan={8} className="py-14 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-14 text-center text-sm text-foreground-500">No invoices match your filters.</td></tr>
              ) : (
                filtered.map((inv) => (
                  <tr key={inv.id} className="hover:bg-background-100/50">
                    <td className={`${adminTd} font-semibold text-foreground-900`}>{inv.invoiceNo}</td>
                    <td className={adminTd}>
                      <p className="text-foreground-800">{inv.customer}</p>
                      <p className="text-xs text-foreground-500">{inv.business || inv.customerEmail || "—"}</p>
                    </td>
                    <td className={adminTd}>{inv.plan}</td>
                    <td className={`${adminTd} tabular-nums font-semibold text-foreground-900`}>
                      ₹{fmt(inv.total)}
                      <span className="block text-xs font-normal text-foreground-500">₹{fmt(inv.amount)} + ₹{fmt(inv.tax)} tax</span>
                    </td>
                    <td className={adminTd}>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${STATUS_BADGE[inv.status]}`}>{inv.status}</span>
                    </td>
                    <td className={adminTd}>
                      {inv.orderNo ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-800">
                          <i className="ri-link" /> {inv.orderNo}
                        </span>
                      ) : (
                        <span className="text-xs text-foreground-400">—</span>
                      )}
                    </td>
                    <td className={`${adminTd} text-xs text-foreground-500`}>{fmtDate(inv.dueAt)}</td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex flex-wrap justify-end gap-1">
                        {inv.status === "draft" && (
                          <button type="button" disabled={busyId === inv.id} onClick={() => apply(inv, "sent")} className={adminIconBtn} title="Mark as sent"><i className="ri-send-plane-line text-base text-secondary-600" /></button>
                        )}
                        {(inv.status === "draft" || inv.status === "sent" || inv.status === "overdue") && (
                          <button type="button" disabled={busyId === inv.id} onClick={() => apply(inv, "paid")} className={adminIconBtn} title="Mark as paid"><i className="ri-money-dollar-circle-line text-base text-emerald-600" /></button>
                        )}
                        <button type="button" disabled={busyId === inv.id} onClick={() => setPrintTarget(inv)} className={adminIconBtn} title="Print / PDF"><i className="ri-printer-line text-base" /></button>
                        <button
                          type="button"
                          disabled={busyId === inv.id || !inv.customerEmail}
                          onClick={() => sendEmail(inv)}
                          className={`${adminIconBtn} disabled:cursor-not-allowed disabled:opacity-40`}
                          title={inv.customerEmail ? "Email invoice" : "Add a customer email first"}
                        >
                          <i className="ri-mail-send-line text-base text-primary-600" />
                        </button>
                        <button type="button" disabled={busyId === inv.id} onClick={() => openEdit(inv)} className={adminIconBtn} title="Edit invoice"><i className="ri-pencil-line text-base" /></button>
                        {inv.status !== "void" && (
                          <button type="button" disabled={busyId === inv.id} onClick={() => apply(inv, "void")} className={adminIconBtn} title="Void invoice"><i className="ri-forbid-line text-base text-rose-600" /></button>
                        )}
                        <button type="button" disabled={busyId === inv.id} onClick={() => setDeleteTarget(inv)} className={adminIconBtn} title="Delete invoice"><i className="ri-delete-bin-line text-base text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {form !== null && (
        <AdminModal
          onClose={closeForm}
          title={editId ? "Edit invoice" : "Add invoice"}
          subtitle={editId ? "Update this billing invoice. Tax and total recalculate automatically." : "Raise a billing invoice for a listing-plan customer. 18% tax is applied automatically."}
          footer={
            <>
              <button type="button" onClick={closeForm} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!form?.customer?.trim() || saving} onClick={submit} className={adminBtnPrimary}>
                {saving ? "Saving…" : editId ? "Save changes" : "Create invoice"}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={adminLabel} htmlFor="in-customer">Customer name</label>
              <input id="in-customer" type="text" value={form.customer} onChange={(e) => set("customer", e.target.value)} className={adminInput} placeholder="e.g. Lakshmi Traders" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="in-email">Customer email</label>
              <input id="in-email" type="email" value={form.customerEmail} onChange={(e) => set("customerEmail", e.target.value)} className={adminInput} placeholder="for sending the invoice" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="in-business">Business</label>
              <input id="in-business" type="text" value={form.business} onChange={(e) => set("business", e.target.value)} className={adminInput} placeholder="e.g. Lakshmi Traders" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="in-plan">Plan</label>
              <input id="in-plan" type="text" value={form.plan} onChange={(e) => set("plan", e.target.value)} className={adminInput} placeholder="e.g. Standard Monthly" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="in-amount">Subtotal (₹)</label>
              <input id="in-amount" type="number" min={0} value={form.amount} onChange={(e) => set("amount", Number(e.target.value) || 0)} className={adminInput} />
            </div>
            <div>
              <label className={adminLabel} htmlFor="in-status">Status</label>
              <select id="in-status" value={form.status} onChange={(e) => set("status", e.target.value as ErpInvoiceStatus)} className={`${adminInput} cursor-pointer`}>
                {CREATE_STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="in-due">Due date</label>
              <input id="in-due" type="date" value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} className={adminInput} />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-background-200 bg-background-100/60 px-4 py-3 text-sm">
            <div>
              <p className="text-xs text-foreground-500">Subtotal</p>
              <p className="font-semibold tabular-nums text-foreground-800">₹{fmt(form.amount)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500">Tax (18%)</p>
              <p className="font-semibold tabular-nums text-foreground-800">₹{fmt(previewTax)}</p>
            </div>
            <div className="col-span-2 flex items-center justify-between border-t border-background-200 pt-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground-600">Total due</span>
              <span className="font-heading text-base font-bold tabular-nums text-foreground-950">₹{fmt(previewTotal)}</span>
            </div>
          </div>

          {formError && (
            <p className="mt-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              <i className="ri-error-warning-line text-base" /> {formError}
            </p>
          )}
        </AdminModal>
      )}

      {deleteTarget !== null && (
        <AdminModal
          onClose={() => setDeleteTarget(null)}
          title="Delete invoice"
          footer={
            <>
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                {busyId === deleteTarget.id ? "Deleting…" : "Delete invoice"}
              </button>
            </>
          }
        >
          <p className="text-sm text-foreground-700">
            Permanently delete <strong>{deleteTarget.invoiceNo}</strong> ({deleteTarget.customer})? This cannot be undone.
          </p>
        </AdminModal>
      )}

      {printTarget !== null && (
        <InvoicePrintModal invoice={printTarget} onClose={() => setPrintTarget(null)} />
      )}
    </div>
  );
}